import { AddressRecord, BookingProposalItemRecord, BrandRecordList, CarWheelIdentifiers } from '@navio/nsdk';
import { SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { useBookingActions } from 'noddi-web';

type params = {
  bookingProposalItems: BookingProposalItemRecord[];
  brand: BrandRecordList;
  address: AddressRecord & { publicAnnouncement?: string | null; brands?: BrandRecordList[] };
  proposalId: number;
  customer: { userGroupId: number; userId: number };
};

export const usePopulateBookingStoreFromProposal = () => {
  const {
    clearStore,
    addCar,
    setSelectedAddress,
    setSelectedSalesItemsCars,
    setBookingProposalId,
    setBookingProposalCustomer
  } = useBookingActions();

  const populateBookingStoreFromProposal = ({ bookingProposalItems, brand, address, proposalId, customer }: params) => {
    // important to clear store as the first thing we do
    clearStore();

    setBookingProposalId(proposalId);
    setBookingProposalCustomer(customer);

    setSelectedAddress({
      ...address,
      publicAnnouncement: '',
      brands: [brand]
    });

    // Add cars
    bookingProposalItems.forEach(({ car, carWheelIdentifiers }) => {
      addCar({
        make: car.make,
        model: car.model,
        hasTireHotel: false, // this holds for now as we dont have tire hotel in proposals...
        hasStuddedWinterTires: carWheelIdentifiers?.identifiers?.includes(CarWheelIdentifiers.IS_STUDDED),
        licensePlate: car.licensePlate
      });
    });

    // Add sales items
    setSelectedSalesItemsCars(
      bookingProposalItems.flatMap(({ car, salesItemsWithQuantity }) =>
        salesItemsWithQuantity.map(({ salesItem }) => ({
          licensePlate: car.licensePlate,
          salesItemId: salesItem.id,
          type: getSalesItemType(false, false, salesItem.isWheelStoragePickup, salesItem.isWheelStorageCancel),
          // TODO FIX - these are used in the store, but we need to set them to false for now
          isWheelPickupRequired: false,
          isAddonsAvailable: false,
          bookingCategoryType: salesItem.bookingCategoryType ?? null
        }))
      )
    );
  };

  return populateBookingStoreFromProposal;
};

function getSalesItemType(
  isPrimary: boolean,
  isAddon: boolean,
  isTireHotelPickup: boolean,
  isTireHotelCancel: boolean
): SALES_ITEM_BOOKING_FLOW_TYPE {
  if (isPrimary) {
    return SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY;
  }

  if (isAddon) {
    return SALES_ITEM_BOOKING_FLOW_TYPE.ADDON;
  }

  if (isTireHotelPickup) {
    return SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP;
  }

  if (isTireHotelCancel) {
    return SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_CANCEL;
  }

  return SALES_ITEM_BOOKING_FLOW_TYPE.PRIMARY;
}
